/**
 * Practido Rule-Based Syntax Validator & Diagnostics Engine
 * Validates Python 3 and JavaScript ES6 code and generates instant educational tips.
 */

export const validateSyntax = (codeText, lang = "python") => {
  if (!codeText || !codeText.trim()) {
    return {
      isValid: true,
      errors: [],
      logs: [{ type: "info", text: "Workspace is empty. Ready for code input." }],
    };
  }

  const lines = codeText.split("\n");
  const errors = [];
  const delimiterStack = []; // Tracks { char, line, col }
  let inMultilineString = false;
  let multilineDelimiter = "";

  for (let i = 0; i < lines.length; i++) {
    const lineNum = i + 1;
    const rawLine = lines[i];
    const trimmed = rawLine.trim();

    // Skip empty lines
    if (!trimmed) continue;

    // Skip comment-only lines
    if (lang === "python" && trimmed.startsWith("#")) continue;
    if (lang !== "python" && trimmed.startsWith("//")) continue;

    // 1. Python Keyword Header Colon Checks
    if (lang === "python") {
      const isHeaderKw = /^(if|elif|while|for|def|class|else|try|finally|except)\b/.test(trimmed);
      if (isHeaderKw) {
        // Strip string literals to check if a colon ':' exists in actual code
        const codeWithoutStrings = trimmed.replace(/"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'/g, '""');
        if (!codeWithoutStrings.includes(":")) {
          // If line ends with continuation character or open paren, it might continue on next line
          if (!trimmed.endsWith("(") && !trimmed.endsWith("[") && !trimmed.endsWith("\\")) {
            const kw = trimmed.split(/[\s(:]/)[0];
            errors.push({
              line: lineNum,
              message: `Missing colon ':' in '${kw}' statement`,
              tip: `In Python, header statements (${kw}) must include a colon ':' (e.g. 'if condition:' or 'def func():').`,
              severity: "error",
            });
          }
        }
      }
    }

    // 2. Token & Delimiter Scanning per Character
    let inString = false;
    let stringQuote = "";
    let escapeNext = false;

    for (let col = 0; col < rawLine.length; col++) {
      const char = rawLine[col];
      const nextChar = col + 1 < rawLine.length ? rawLine[col + 1] : "";
      const thirdChar = col + 2 < rawLine.length ? rawLine[col + 2] : "";

      if (escapeNext) {
        escapeNext = false;
        continue;
      }

      if (char === "\\") {
        escapeNext = true;
        continue;
      }

      // Handle Triple Quotes in Python
      if (lang === "python" && (char === '"' || char === "'") && nextChar === char && thirdChar === char) {
        const triple = char + nextChar + thirdChar;
        if (!inMultilineString) {
          inMultilineString = true;
          multilineDelimiter = triple;
        } else if (multilineDelimiter === triple) {
          inMultilineString = false;
          multilineDelimiter = "";
        }
        col += 2;
        continue;
      }

      if (inMultilineString) continue;

      // Handle Regular Single Line Quotes
      if (char === '"' || char === "'" || (lang !== "python" && char === "`")) {
        if (!inString) {
          inString = true;
          stringQuote = char;
        } else if (stringQuote === char) {
          inString = false;
          stringQuote = "";
        }
        continue;
      }

      if (inString) continue;

      // Handle inline comments
      if (lang === "python" && char === "#") break;
      if (lang !== "python" && char === "/" && nextChar === "/") break;

      // Push open delimiters
      if (char === "(" || char === "[" || char === "{") {
        delimiterStack.push({ char, line: lineNum, col: col + 1 });
      }

      // Check matching close delimiters
      if (char === ")" || char === "]" || char === "}") {
        if (delimiterStack.length === 0) {
          const matchName = char === ")" ? "parenthesis" : char === "]" ? "bracket" : "brace";
          errors.push({
            line: lineNum,
            message: `Unexpected closing ${matchName} '${char}'`,
            tip: `Check for extra or misplaced '${char}' without a matching opening delimiter.`,
            severity: "error",
          });
        } else {
          const last = delimiterStack.pop();
          const expected = last.char === "(" ? ")" : last.char === "[" ? "]" : "}";
          if (char !== expected) {
            errors.push({
              line: lineNum,
              message: `Mismatched delimiter: expected '${expected}' to close '${last.char}' from Line ${last.line}, but found '${char}'`,
              tip: `Ensure delimiters match: () for parentheses, [] for lists, and {} for code blocks/dictionaries.`,
              severity: "error",
            });
          }
        }
      }
    }

    // Check for unclosed string on same line
    if (inString && !inMultilineString) {
      errors.push({
        line: lineNum,
        message: `Unclosed string literal starting with ${stringQuote}`,
        tip: `Make sure to close strings with a matching ${stringQuote} before the end of the line.`,
        severity: "error",
      });
    }
  }

  // 3. Check for any remaining unclosed delimiters across entire file
  if (delimiterStack.length > 0) {
    const unclosed = delimiterStack[delimiterStack.length - 1];
    const matchName = unclosed.char === "(" ? "parenthesis ')'" : unclosed.char === "[" ? "bracket ']'" : "brace '}'";
    errors.push({
      line: unclosed.line,
      message: `Unclosed delimiter '${unclosed.char}' opened on Line ${unclosed.line}`,
      tip: `Add a closing ${matchName} to complete the statement.`,
      severity: "error",
    });
  }

  if (inMultilineString) {
    errors.push({
      line: lines.length,
      message: `Unclosed multiline docstring starting with ${multilineDelimiter}`,
      tip: `Close the multiline string with ${multilineDelimiter}.`,
      severity: "error",
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};
